import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PROJECTS = [
    {
        img: "/images/soft-work-brand.png",
        title: "Brand Identity",
        tags: ["Logo", "Visual Identity", "Brand System"],
    },
    {
        img: "/images/soft-work-social.png",
        title: "Campaign Creative",
        tags: ["Art Direction", "Social", "Advertising"],
    },
    {
        img: "/images/soft-work-marketing.png",
        title: "Editorial Design",
        tags: ["Visual Story", "Content", "Brand Art"],
    },
    {
        img: "/images/soft-work-web.png",
        title: "Logo & Marks",
        tags: ["Logos", "Illustration", "Brand Assets"],
    },
];

const WorkCard = ({ p, i }) => (
    <Reveal delay={i * 0.08} className="h-full">
        <motion.article
            data-testid={`work-card-${i}`}
            whileHover={{ y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="group h-full bg-[#0C0C0C] rounded-3xl border border-[#262626] overflow-hidden shadow-[0_10px_40px_-18px_rgba(12,12,12,0.35)]"
        >
            <div className="overflow-hidden bg-[#0C0C0C] p-3">
                <img
                    src={p.img}
                    alt={`${p.title} by Savv Media`}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover rounded-2xl transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
            </div>
            <div className="p-6 md:p-7 flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-serif-ed text-2xl tracking-tight text-[#F1F1EF]">
                        {p.title}
                    </h3>
                    <p className="mt-1.5 text-xs font-medium tracking-wide text-[#A9A6A0]">
                        {p.tags.join(" · ")}
                    </p>
                </div>
                <span className="mt-1 shrink-0 w-10 h-10 rounded-full border border-[#3A3936] flex items-center justify-center text-[#F1F1EF] group-hover:bg-[#D3A0A7] group-hover:border-[#D3A0A7] group-hover:text-[#0C0C0C] transition-colors duration-300">
                    <ArrowUpRight size={16} />
                </span>
            </div>
        </motion.article>
    </Reveal>
);

export const Work = () => (
    <section
        id="work"
        data-testid="work-section"
        className="py-20 md:py-32 px-5 sm:px-8 lg:px-12"
    >
        <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-14 md:mb-20">
                <div className="max-w-xl">
                    <Reveal>
                        <Eyebrow>Featured Work</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-4 font-serif-ed text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#F1F1EF]">
                            The work speaks{" "}
                            <span className="italic text-[#D3A0A7]">
                                for itself.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <Reveal delay={0.2}>
                    <a
                        href="#contact"
                        data-testid="work-see-all-link"
                        className="group inline-flex items-center gap-2 font-semibold text-sm text-[#F1F1EF] link-underline"
                    >
                        See all the work
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1"
                        />
                    </a>
                </Reveal>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
                {PROJECTS.map((p, i) => (
                    <WorkCard key={p.title} p={p} i={i} />
                ))}
            </div>

            <Reveal delay={0.15} className="mt-10">
                <div className="relative rounded-[2rem] overflow-hidden border-4 border-[#F1F1EF] shadow-[0_30px_70px_-24px_rgba(12,12,12,0.35)]">
                    <img
                        src="/images/soft-social-grid.png"
                        alt="Savv Media social media grid showcase"
                        data-testid="work-social-grid-image"
                        loading="lazy"
                        className="w-full h-52 md:h-72 object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-[#0C0C0C]/35 flex items-center justify-between px-6 md:px-12">
                        <p className="font-hand text-3xl md:text-4xl text-[#EAF1F7] rotate-[-2deg]">
                            Scroll-stopping, always ♡
                        </p>
                        <a
                            href="#contact"
                            data-testid="work-grid-cta"
                            className="hidden sm:inline-flex items-center gap-2 bg-[#F1F1EF] text-[#0C0C0C] text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#C9DDEE] transition-colors duration-300"
                        >
                            Start yours <ArrowRight size={15} />
                        </a>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
